<template>
  <div class="container-page">
    <p class="title-inner">Инвестиции</p>
    <div class="mb-16 investments__buttons">
      <ButtonComponent
        v-permission="permissions.import"
        mod="gradient-bg"
        :disabled="importInProgress"
        @click="importExcel()"
      >
        {{ !importInProgress ? 'Загрузить из Excel' : 'Загрузка...' }}
      </ButtonComponent>
    </div>
    <DefaultFilter class="mb-16" :filterValue="filter" @loadFilter="onLoadFilter" />
    <div v-if="isAccessAllowed">
      <div class="table-container mb-16 js-table-arrow">
        <table class="stockTable">
          <thead>
            <tr>
              <th>Год</th>
              <th>Страна</th>
              <th>Приток ПИИ, млн $ США</th>
              <th>Вывоз ПИИ, млн $ США</th>
              <th>Накопленные входящие ПИИ, млн $ США</th>
              <th>Накопленные исходящие ПИИ, млн $ США</th>
              <th>Стоимость международных СиП (продажи), млн $ США</th>
              <th>Стоимость международных СиП (приобретения), млн $ США</th>
              <th>Стоимость инвестиций в новые проекты (из страны), млн $ США</th>
              <th>Стоимость инвестиций в новые проекты (в страну), млн $ США</th>
            </tr>
            <tr>
              <th class="filter">
                <FilterInput
                  type="year"
                  v-model="filter.year"
                  placeholder="Год"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <FilterInput
                  type="select2"
                  :select2Options="countries"
                  :select2Settings="select2Settings"
                  v-model="filter.countryId"
                  placeholder="(Все)"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.inFlowsFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.inFlowsTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.outFlowsFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.outFlowsTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.inStockFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.inStockTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.outStockFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.outStockTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.maSalesFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.maSalesTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.maPurchasesFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.maPurchasesTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.outProjectsFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.outProjectsTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.inProjectsFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.inProjectsTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody v-if="page.items?.length && !isLoading">
            <tr v-for="item in page.items" :key="item.id">
              <td>{{ item.year }}</td>
              <td>{{ item.country.nameFull || item.country.name }}</td>
              <td>{{ item.inFlows }}</td>
              <td>{{ item.outFlows }}</td>
              <td>{{ item.inStock }}</td>
              <td>{{ item.outStock }}</td>
              <td>{{ item.maSales }}</td>
              <td>{{ item.maPurchases }}</td>
              <td>{{ item.outProjects }}</td>
              <td>{{ item.inProjects }}</td>
            </tr>
          </tbody>
          <tbody v-else-if="isLoading">
            <tr>
              <td class="text-center" colspan="10">Загрузка...</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="10">Нет данных.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="page.pagesTotal > 1">
        <Pagination
          :pageCount="page.pagesTotal"
          :currentPage="page.pageNumber"
          @change="onPageChange($event, false)"
          @more="onPageChange($event, true)"
        />
      </div>
    </div>
    <section>
      <ModalComponent v-model="importModal" title="Импорт файла" @close="closeModal">
        <div>
          <input type="text" v-model="importYear" placeholder="Год" />
        </div>
        <ImportModal @doimport="doImport" @close="closeModal" />
      </ModalComponent>
    </section>
  </div>
</template>

<script>
  import DefaultFilter from '@/common/components/DefaultFilter';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import ImportModal from '@/components/modals/ImportModal.vue';

  import FilterInput from '../../../common/components/FilterInput.vue';
  import ModalComponent from '../../../components/modals/ModalComponent.vue';
  import Api from '../api/index';

  const PERMISSIONS = {
    view: Constants.Permissions.InvestmentView,
    import: Constants.Permissions.InvestmentImport,
  };

  export default {
    name: 'InvestmentList',
    components: { FilterInput, ImportModal, ModalComponent, ButtonComponent, DefaultFilter },
    mixins: [security],
    data() {
      return {
        route: 'investment',
        page: {},
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        filter: {
          year: null,
          countryId: null,
          inFlowsFrom: null,
          inFlowsTo: null,
          outFlowsFrom: null,
          outFlowsTo: null,
          inStockFrom: null,
          inStockTo: null,
          outStockFrom: null,
          outStockTo: null,
          maSalesFrom: null,
          maSalesTo: null,
          maPurchasesFrom: null,
          maPurchasesTo: null,
          outProjectsFrom: null,
          outProjectsTo: null,
          inProjectsFrom: null,
          inProjectsTo: null,
        },
        countries: [],
        select2Settings: Constants.select2Settings,
        importInProgress: false,
        importModal: false,
        importYear: '',
        isLoading: false,
      };
    },
    computed: {
      permissions: () => PERMISSIONS,
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then((isAllowed) => {
        if (!isAllowed) return;

        loadFilter(this.$options.name).then((response) => {
          let filter = response;
          if (filter) {
            this.filter = filter;
          }
          this.loadDictionaries();
          this.loadPage();
        });
      });
    },
    methods: {
      loadPage(append) {
        this.isLoading = !append;
        const request = Object.assign(this.filter, this.request);
        Api.search(this.route, request)
          .then((response) => {
            if (!append) {
              this.page = {};
              this.$nextTick(() => {
                this.page = response.data;
              });
            } else if (response.data) {
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      loadDictionaries() {
        Api.search('nsi/macroeconomicindicator', { pageNumber: 1, pageSize: 0, isActive: true })
          .then((response) => {
            this.indicators = response.data.items.map((x) => ({ id: x.id, text: x.name }));
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
        Utils.searchNsi('countrylistwithdata', {})
          .then((response) => {
            this.countries = response.data.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
      },
      importExcel() {
        this.importModal = true;
      },
      doImport(file) {
        if (!this.importYear) {
          Constants.alert.fire('Импорт', 'Введите год для импорта данных.', 'error');
          return;
        }

        this.closeModal();
        this.importInProgress = true;
        Api.uploadInvestmentsFile(this.importYear, file)
          .then(() => {
            Constants.alert.fire('Импорт', 'Импорт успешно выполнен.', 'success');
            this.loadPage();
            this.importInProgress = false;
          })
          .catch((error) => {
            console.log(error?.response?.data);
            Constants.alert.fire('Импорт', 'Ошибка при импорте данных.', 'error');
            this.importInProgress = false;
          });
      },
      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      closeModal() {
        this.importModal = false;
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
    },
  };
</script>

<style lang="scss">
  .investments__buttons {
    display: flex;
    gap: 12px;
  }

  .filter {
    .range {
      display: flex;
      .input-group-wrapper {
        input {
          padding: 4px 8px;
          min-width: 80px;
          max-width: 80px;
        }
      }
    }
  }
</style>
